import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, Tag } from 'lucide-react';
import { formatUZS } from '@/lib/format';
import { ProductInfoPanel } from '@/components/products/ProductInfoPanel';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  product: any | null;
  originalPrice: number;
  onSave: (values: { price: number; sale_price: number }) => Promise<void> | void;
  saving?: boolean;
}

export function PriceEditDialog({ open, onOpenChange, product, originalPrice, onSave, saving }: Props) {
  const { t } = useTranslation();
  const [price, setPrice] = useState('');
  const [salePrice, setSalePrice] = useState('');

  useEffect(() => {
    if (open && product) {
      setPrice(originalPrice > 0 ? String(originalPrice) : '');
      setSalePrice(String(Number(product.sale_price) || 0));
    }
  }, [open, product, originalPrice]);

  const p = Number(price) || 0;
  const sp = Number(salePrice) || 0;
  const discount = sp > 0 && p > 0 && p > sp ? p - sp : 0;
  const discountPercent = discount > 0 ? Math.round((discount / p) * 100) : 0;

  const invalid = Number(price) < 0 || Number(salePrice) < 0 || isNaN(Number(price)) || isNaN(Number(salePrice));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-none w-screen h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none p-0 gap-0 flex flex-col overflow-hidden">
        <div className="flex flex-col h-full min-h-0 max-w-6xl mx-auto w-full">
          <DialogHeader className="shrink-0 px-6 py-5 border-b text-left">
            <DialogTitle className="text-lg">{product?.name || t('cat_pricing.productPrice')}</DialogTitle>
            <p className="text-sm text-muted-foreground font-mono">{product?.sku}</p>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-6">
            <ProductInfoPanel product={product} />

            <div className="rounded-xl border border-border bg-card">
              <div className="px-4 py-3 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">{t('cat_pricing.prices')}</h3>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="orig-price">{t('cat_pricing.originalPriceSom')}</Label>
                  <Input
                    id="orig-price"
                    type="number"
                    min={0}
                    value={price}
                    onChange={e => setPrice(e.target.value)}
                    className="h-11 text-base"
                  />
                  <p className="text-xs text-muted-foreground">{formatUZS(p)}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sale-price" className="flex items-center gap-1"><Tag size={12} /> {t('cat_pricing.discountPriceSom')}</Label>
                  <Input
                    id="sale-price"
                    type="number"
                    min={0}
                    value={salePrice}
                    onChange={e => setSalePrice(e.target.value)}
                    className="h-11 text-base"
                  />
                  <p className="text-xs text-muted-foreground">{formatUZS(sp)}</p>
                </div>
                <div className="sm:col-span-2 flex items-center gap-3 pt-2 border-t border-border">
                  <span className="text-sm text-muted-foreground">{t('cat_pricing.discountColon')}</span>
                  {discount > 0 ? (
                    <Badge variant="secondary" className="text-sm font-mono">
                      −{formatUZS(discount)} ({discountPercent}%)
                    </Badge>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('cat_pricing.noDiscount')}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 mt-0 px-6 py-4 border-t bg-card flex flex-row flex-wrap items-center justify-between gap-3">
            <Button
              variant="outline"
              onClick={() => setSalePrice('0')}
              disabled={saving || sp === 0}
            >
              {t('cat_pricing.removeDiscount')}
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('cat_common.cancel')}</Button>
              <Button
                onClick={() => onSave({ price: Number(price) || 0, sale_price: Number(salePrice) || 0 })}
                disabled={saving || invalid}
              >
                {saving ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('cat_common.saving')}</> : t('cat_common.save')}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
